import axios from "../helpers/axios";

// Types for responses and requests
export interface IManagerInvitation {
  _id: string;
  invitationType: string;
  status: "pending" | "accepted" | "rejected";
  message: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  sender: {
    _id: string;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    type: string;
    avatarPath: string;
  };
  recipient: {
    _id: string;
    type: string;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    avatarPath: string;
    name: string;
  };
}

export interface ICurrentManager {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  type: string;
  avatarPath: string;
  since: string;
}

class ManagerService {
  /**
   * Respond to a manager invitation (accept or reject)
   * @param invitationId Invitation ID
   * @param status "accepted" or "rejected"
   */
  public respondToInvitation = (invitationId: string, status: "accepted" | "rejected") => {
    const url = `/business/invitation/creator/${invitationId}/respond`;
    return new Promise<{ message: string }>((resolve, reject) => {
      axios
        .patch(url, { status })
        .then((response) => {
          if (response?.data) resolve(response.data);
          else reject("Une erreur s'est produite.");
        })
        .catch((error) => {
          console.error('Error responding to invitation:', error);
          if (error?.response?.status === 404) {
            reject("L'invitation n'a pas été trouvée ou a déjà été traitée.");
          } else if (error?.response?.data?.message) {
            reject(error.response.data.message);
          } else {
            reject("Une erreur s'est produite.");
          }
        });
    });
  };

  /**
   * Delete the current manager
   * @param managerId ID of the manager to delete
   */
  public deleteCurrentManager = (managerId: string) => {
    const url = `/business/remove-me/${managerId}`;
    return new Promise<{ message: string }>((resolve, reject) => {
      axios
        .delete(url)
        .then((response) => {
          if (response?.data) resolve(response.data);
          else reject("Une erreur s'est produite.");
        })
        .catch((error) => {
          console.error('Error deleting manager:', error);
          if (error?.response?.status === 404) {
            reject("Le manager n'a pas été trouvé ou a déjà été supprimé.");
          } else if (error?.response?.data?.message) {
            reject(error.response.data.message);
          } else {
            reject("Une erreur s'est produite.");
          }
        });
    });
  };

  /**
   * Get manager invitations
   */
  public getPendingInvitations = () => {
    const url = `/business/invitation/creator`;
    return new Promise<IManagerInvitation[]>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data) {
            const pendingInvitations = response.data.filter(
              (invitation: IManagerInvitation) => 
                invitation.status === "pending" && 
                invitation.invitationType === "manager-to-creator"
            );
            resolve(pendingInvitations);
          } else reject("Une erreur s'est produite.");
        })
        .catch((error) => {
          console.error('Error getting invitations:', error);
          if (error?.response?.status === 404) {
            resolve([]);
          } else if (error?.response?.data?.message) {
            reject(error.response.data.message);
          } else {
            reject("Une erreur s'est produite.");
          }
        });
    });
  };

  /**
   * Get the current manager
   */
  public getCurrentManager = () => {
    const url = `/business/creator/details`;
    return new Promise<ICurrentManager | null>((resolve, reject) => {
      axios
        .get(url)
        .then((response) => {
          if (response?.data?.managerDetails) {
            resolve(response.data.managerDetails);
          } else resolve(null);
        })
        .catch((error) => {
          console.error('Error getting current manager:', error);
          if (error?.response?.status === 404) {
            resolve(null);
          } else if (error?.response?.data?.message) {
            reject(error.response.data.message);
          } else {
            reject("Une erreur s'est produite.");
          }
        });
    });
  };
}

export const managerService = new ManagerService();
